import {
  Button,
  Css,
  GridColumn,
  GridTable,
  IconButton,
  ScrollableContent,
  simpleDataRows,
  SimpleHeaderAndData,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { useParams } from "react-router-dom";
import { DropDetailsFragment, useDeleteDropMutation } from "src/components/drops/DropsPage.generated";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { EmptyState } from "src/routes/components/EmptyState";
import { EmptyPlanAndOptionImage } from "src/routes/developments/plan-and-options/components/EmptyStateSvgs";
import { PageHeader } from "src/routes/layout/PageHeader";
import { createProductOfferingsUrl } from "src/RouteUrls";
import { openNewTab } from "src/utils/window";
import { DropModal } from "./DropModal";

export type DropsPageProps = { drops: DropDetailsFragment[] };

type Row = SimpleHeaderAndData<DropDetailsFragment>;

export function DropsPageView({ drops }: DropsPageProps) {
  const { developmentId, projectId } = useParams<{ developmentId: string; projectId: string }>();
  const { openModal } = useModal();
  const [deleteDrop] = useDeleteDropMutation({ refetchQueries: ["DropsPage"] });
  const hasNoDrops = drops.length === 0;
  const { triggerNotice } = useSnackbar();

  function createDrop() {
    openModal({ content: <DropModal developmentId={developmentId} projectId={projectId} /> });
  }

  function editDrop(drop: DropDetailsFragment) {
    openModal({ content: <DropModal existingDrop={drop} developmentId={developmentId} projectId={projectId} /> });
  }

  function deleteDropHandler(drop: DropDetailsFragment) {
    openModal({
      content: (
        <ConfirmationModal
          confirmationMessage={
            <>
              Are you sure you want to delete this Drop{" "}
              <span css={Css.smSb.$}>
                {drop.code} {drop.name}
              </span>
              ? It will not be archived and cannot be undone.
            </>
          }
          onConfirmAction={async () => {
            await deleteDrop({ variables: { id: drop.id } });

            triggerNotice({
              message: (
                <span>
                  Drop changes to product offerings can be accepted on the &nbsp;
                  <span css={Css.tdu.$} onClick={() => openNewTab(createProductOfferingsUrl(developmentId, projectId))}>
                    Offerings page
                  </span>
                  &nbsp; now.
                </span>
              ),
            });
          }}
          title="Delete Drop"
          label="Yes, Delete"
          danger={true}
        />
      ),
    });
  }

  return (
    <div>
      <PageHeader title="Drops" right={hasNoDrops ? null : <Button label="Add another Drop" onClick={createDrop} />} />
      {hasNoDrops ? (
        <EmptyState
          message="There are no Drops yet"
          svg={<EmptyPlanAndOptionImage />}
          button={<Button label="Create New Drop" onClick={createDrop} />}
        />
      ) : (
        <ScrollableContent>
          <GridTable columns={createColumns(editDrop, deleteDropHandler)} rows={simpleDataRows(drops)} />
        </ScrollableContent>
      )}
    </div>
  );
}

function createColumns(
  editDrop: (drop: DropDetailsFragment) => void,
  deleteDrop: (drop: DropDetailsFragment) => void,
): GridColumn<Row>[] {
  return [
    { header: "Drop Code", data: ({ code }) => code },
    { header: "Drop Name", data: ({ name }) => name },
    { header: "Cost Code", data: ({ costCode }) => costCode.displayName },
    { header: "Drop Task", data: ({ globalPlanTask }) => globalPlanTask.nameForEntity },
    {
      header: "Locations",
      data: ({ locations }) => (
        <div css={Css.oh.add("textOverflow", "ellipsis").onHover.ov.$}>{locations.map((l) => l.name).join(", ")}</div>
      ),
    },
    {
      header: "Edit",
      data: (drop) => (
        <div css={Css.df.gap1.$}>
          <IconButton icon="pencil" onClick={() => editDrop(drop)} />
          <IconButton icon="trash" onClick={() => deleteDrop(drop)} />
        </div>
      ),
      w: "100px",
    },
  ];
}
